export type ReportFileRef = {
  id?: number;
  url?: string;
  api_url?: string;
  name?: string;
  type?: string;
};

/**
 * Builds the authenticated API URL for a report file.
 */
export function reportFileApiUrl(reportId: number, apiBaseUrl: string): string {
  const base = apiBaseUrl.replace(/\/$/, '');
  return `${base}/reports/${reportId}/file`;
}

export function normalizeReportAssetUrl(
  url: string | null | undefined,
  apiBaseUrl: string,
): string {
  const raw = String(url || '').trim();
  if (!raw) {
    return '';
  }

  let normalized = raw.replace('/storage/app/public/', '/storage/');

  try {
    const baseOrigin = new URL(apiBaseUrl.endsWith('/') ? apiBaseUrl : `${apiBaseUrl}/`).origin;

    if (/^https?:\/\/(localhost|127\.0\.0\.1)/i.test(normalized)) {
      const parsed = new URL(normalized);
      if (!parsed.port || parsed.port === '80' || parsed.port === '443') {
        return `${baseOrigin}${parsed.pathname}${parsed.search}`;
      }
      return normalized;
    }

    if (!/^https?:\/\//i.test(normalized)) {
      const path = normalized.startsWith('/') ? normalized : `/${normalized}`;
      return `${baseOrigin}${path}`;
    }
  } catch (e) {
    // Fallback if URL parsing fails
  }

  return normalized;
}

/**
 * Resolves the best URL to use for a report file in the UI.
 */
export function resolveReportFileUrl(
  fileOrUrl: string | ReportFileRef | null | undefined,
  apiBaseUrl: string,
): string {
  if (!fileOrUrl) {
    return '';
  }

  if (typeof fileOrUrl === 'object') {
    if (fileOrUrl.url) {
      return normalizeReportAssetUrl(fileOrUrl.url, apiBaseUrl);
    }
    if (fileOrUrl.id) {
      return reportFileApiUrl(fileOrUrl.id, apiBaseUrl);
    }
    return '';
  }

  return normalizeReportAssetUrl(fileOrUrl, apiBaseUrl);
}

export function isImageReportFile(file: { type?: string } | null | undefined): boolean {
  return String(file?.type || '').toLowerCase().startsWith('image/');
}

export function isErrorBlob(blob: Blob | null | undefined): boolean {
  if (!blob?.size) {
    return true;
  }
  const type = String(blob.type || '').toLowerCase();
  return type.includes('json') || type.includes('html') || type.includes('text/plain');
}

export async function readBlobErrorMessage(
  blob: Blob | null | undefined,
  fallback = 'Document is unavailable.',
): Promise<string> {
  if (!blob?.size) {
    return fallback;
  }

  const type = String(blob.type || '').toLowerCase();
  if (!type.includes('json') && !type.includes('text')) {
    return fallback;
  }

  try {
    const text = await blob.text();
    if (!text.trim()) {
      return fallback;
    }

    try {
      const payload = JSON.parse(text) as { message?: string };
      if (typeof payload?.message === 'string' && payload.message.trim()) {
        return payload.message.trim();
      }
    } catch {
      if (text.trim().length <= 240) {
        return text.trim();
      }
    }
  } catch {
    return fallback;
  }

  return fallback;
}

export function toTypedBlob(blob: Blob, mimeType?: string): Blob {
  if (blob.type || !mimeType) {
    return blob;
  }
  return new Blob([blob], { type: mimeType });
}

export function downloadBlob(blob: Blob, fileName?: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName || 'document';
  link.rel = 'noopener noreferrer';
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

/**
 * Opens a blank tab synchronously (while the click is active), then loads the blob.
 * Avoids popup blockers that block async window.open calls after HTTP requests.
 */
export function openBlobInPreviewWindow(blob: Blob, file?: ReportFileRef | null): Window | null {
  const typedBlob = toTypedBlob(blob, file?.type);
  const blobUrl = URL.createObjectURL(typedBlob);
  const previewWindow = window.open('about:blank', '_blank');

  if (previewWindow) {
    previewWindow.document.title = file?.name || 'Document';
    previewWindow.location.replace(blobUrl);
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 120_000);
    return previewWindow;
  }

  downloadBlob(typedBlob, file?.name);
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  return null;
}

export function createReportPreviewWindow(file?: ReportFileRef | null): Window | null {
  const previewWindow = window.open('about:blank', '_blank');
  if (!previewWindow) {
    return null;
  }

  previewWindow.document.title = file?.name || 'Document';
  previewWindow.document.body.innerHTML =
    '<p style="font-family:sans-serif;padding:24px;color:#334155;">Loading document...</p>';
  return previewWindow;
}

export function loadBlobIntoPreviewWindow(
  previewWindow: Window | null,
  blob: Blob,
  file?: ReportFileRef | null,
): void {
  if (isErrorBlob(blob)) {
    previewWindow?.close();
    throw new Error('Document is empty or unavailable.');
  }

  const typedBlob = toTypedBlob(blob, file?.type);
  const blobUrl = URL.createObjectURL(typedBlob);

  if (previewWindow && !previewWindow.closed) {
    previewWindow.location.replace(blobUrl);
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 120_000);
    return;
  }

  downloadBlob(typedBlob, file?.name);
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}
